/**
 * WalletConnect 配置
 * 使用 Reown AppKit Core API
 */

import type { AppKitNetwork } from '@reown/appkit/networks'
import type { CustomCaipNetwork } from '@reown/appkit-common'
import { UniversalConnector } from '@reown/appkit-universal-connector'
import { createAppKit } from '@reown/appkit'
import { env } from './env'

// 獲取 Project ID
export const projectId = env.walletConnectProjectId

if (!projectId || projectId === 'YOUR_PROJECT_ID_HERE') {
  throw new Error('WalletConnect Project ID 未設置。請在環境變數中設置 VITE_WALLETCONNECT_PROJECT_ID')
}

// Ethereum Mainnet 配置
const ethereumMainnet: CustomCaipNetwork<'eip155'> = {
  id: 1,
  chainNamespace: 'eip155' as const,
  caipNetworkId: 'eip155:1',
  name: 'Ethereum',
  nativeCurrency: { 
    name: 'Ether', 
    symbol: 'ETH', 
    decimals: 18 
  },
  rpcUrls: { 
    default: { 
      http: ['https://cloudflare-eth.com'] 
    } 
  }
}

// Ethereum Sepolia Testnet 配置
const ethereumSepolia: CustomCaipNetwork<'eip155'> = {
  id: 11155111,
  chainNamespace: 'eip155' as const,
  caipNetworkId: 'eip155:11155111',
  name: 'Sepolia',
  nativeCurrency: { 
    name: 'Sepolia Ether', 
    symbol: 'SEP', 
    decimals: 18 
  },
  rpcUrls: { 
    default: { 
      http: ['https://rpc.sepolia.org'] 
    } 
  }
}

// BSC (Binance Smart Chain) 主網配置
// 注意：RPC 節點可以通過環境變數 VITE_BSC_RPC_URL 自定義
// 如果設置了自定義 RPC，將優先使用；否則使用默認的 Binance RPC 節點
const getBSCRpcUrls = (): string[] => {
  const customRpc = (import.meta as any).env?.VITE_BSC_RPC_URL
  if (customRpc) {
    // 如果設置了自定義 RPC，使用它（可以是用逗號分隔的多個 URL）
    return customRpc.split(',').map((url: string) => url.trim()).filter(Boolean)
  }
  // 默認使用 Binance 官方 RPC 節點
  return [
    'https://bsc-dataseed1.binance.org',
    'https://bsc-dataseed2.binance.org',
    'https://bsc-dataseed3.binance.org',
    'https://bsc-dataseed4.binance.org'
  ]
}

export const bscMainnet: CustomCaipNetwork<'eip155'> = {
  id: 56,
  chainNamespace: 'eip155' as const,
  caipNetworkId: 'eip155:56',
  name: 'BNB Smart Chain',
  nativeCurrency: { 
    name: 'BNB', 
    symbol: 'BNB', 
    decimals: 18 
  },
  rpcUrls: { 
    default: { 
      http: getBSCRpcUrls()
    } 
  },
  blockExplorers: {
    default: {
      name: 'BscScan',
      url: 'https://bscscan.com'
    }
  }
}

// 支持的網絡列表（只保留 BSC 主網）
export const networks = [bscMainnet] as [AppKitNetwork, ...AppKitNetwork[]]

// 應用元數據
export const metadata = {
  name: 'Bentana',
  description: 'Bentana Web3 資金募集平台',
  url: typeof window !== 'undefined' ? window.location.origin : 'https://bentana.com',
  icons: [
    typeof window !== 'undefined' 
      ? `${window.location.origin}/favicon.ico` 
      : 'https://bentana.com/favicon.ico'
  ]
}

/**
 * 獲取 Universal Connector 實例（單例模式）
 * 保留作為備用方案
 */
let universalConnectorInstance: UniversalConnector | null = null

export async function getUniversalConnector(): Promise<UniversalConnector> {
  if (universalConnectorInstance) {
    return universalConnectorInstance
  }

  // 🔥 關鍵：只使用 BSC 主網
  universalConnectorInstance = await UniversalConnector.init({
    projectId,
    metadata,
    networks: [
      {
        methods: [
          'eth_sendTransaction',
          'eth_signTransaction',
          'eth_sign',
          'personal_sign',
          'eth_signTypedData',
          'eth_signTypedData_v4',
          'wallet_switchEthereumChain',
          'wallet_addEthereumChain'
        ],
        chains: [bscMainnet] as CustomCaipNetwork[], // 🔥 只包含 BSC 主網
        events: ['chainChanged', 'accountsChanged'],
        namespace: 'eip155'
      }
    ]
  })

  console.log('✅ UniversalConnector 已初始化，只支持 BSC 主網 (Chain ID: 56)')

  return universalConnectorInstance
}

/**
 * 獲取 AppKit 實例（單例模式）
 * 使用 AppKit Core API 實現直接連接特定錢包
 */
let appKitInstance: ReturnType<typeof createAppKit> | null = null

export function getAppKit() {
  if (appKitInstance) {
    return appKitInstance
  }

  if (typeof window === 'undefined') {
    throw new Error('AppKit 只能在瀏覽器環境中使用')
  }

  // 🔥 關鍵：只使用 BSC 主網
  const bscOnly = [bscMainnet] as [AppKitNetwork, ...AppKitNetwork[]]

  // 只顯示指定的錢包，提升用戶體驗
  const includeWalletIds = [
    '971e689d0a5be027bac1ccc957079eabfcb4af845ac7e0001', // OKX 錢包
    'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa', // 幣安錢包
    'c57ca95b47569778a828d19178114f4cf188f372', // MetaMask
  ]

  appKitInstance = createAppKit({
    projectId,
    metadata,
    networks: bscOnly, // 🔥 只傳入 BSC 主網
    features: {
      analytics: false,
      email: false,
      socials: false
    },
    includeWalletIds,
    enableEIP6963: true,
    enableInjected: true
  })

  console.log('✅ AppKit 已初始化，只支持 BSC 主網 (Chain ID: 56)')

  return appKitInstance
}

